import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector } from '@/store'
import { useRewards } from '@/hooks/useRewards'
import { getDigitalCodesList, deleteDigitalCodes } from '@/services/digitalCodes'
import { getComposedData } from '@/services/transformers/digitalCodesTransformer'
import { REWARD_STATUS } from '@/utils/constants/rewardsConstants'
import { useDataTable } from '@/hooks/useDataTable-hook'

const DIGITAL_CODES_PAGE_SIZE = 10

export function useDigitalCodesCard({ rewardStatus = REWARD_STATUS.DRAFT }) {
  const [allRows, setAllRows] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [selectedRows, setSelectedRows] = useState([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteIds, setPendingDeleteIds] = useState([])
  const [loading, setLoading] = useState(false)
  const mountedRef = useRef(true)

  // Use shared data-table hook for pagination, filters, and sorting (API-based), like useSalesTargets
  const {
    searchArr,
    sortingObj,
    pageIndex,
    setPageIndex,
    setSearchArry,
    setSortingObj,
    handleResetAll: tableHandleResetAll,
  } = useDataTable()

  const rewardId = useAppSelector(state => state.rewards?.formData?.rewardId)
  const rewardName = useAppSelector(state => state.rewards?.formData?.name)
  const { handleDraft } = useRewards()


  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    params.append('pageSize', DIGITAL_CODES_PAGE_SIZE)
    params.append('page', pageIndex || 0)

    // API-based sorting
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('order', sortingObj.direction)
    }

    if (rewardId) {
      params.append('rewardId', rewardId)
    }

    return params.toString()
  }, [pageIndex, rewardId, sortingObj?.column, sortingObj?.direction])

  const normaliseExpiryDateFilter = useCallback((filters = []) => {
    if (!Array.isArray(filters)) return filters

    return filters.map(filter => {
      if (!filter || filter.field !== 'expiryDate' || filter.type !== 'dateRange') return filter

      const raw = filter.value
      if (!raw || typeof raw !== 'string') return filter

      const [from, to] = raw.split(',')
      const trimToDate = (s) => {
        if (!s) return ''
        // Expecting an ISO-like string; backend only wants YYYY-MM-DD
        return s.slice(0, 10)
      }

      return {
        ...filter,
        value: `${trimToDate(from)},${trimToDate(to)}`,
      }
    })
  }, [])

  const loadData = useCallback(async () => {
    // Do not call the API unless a rewardId is available
    if (!rewardId) {
      setAllRows([])
      setTotalRecords(0)
      return
    }

    setLoading(true)
    try {
      const baseFilters = Array.isArray(searchArr) ? searchArr : (searchArr ? [searchArr] : [])
      const filters = normaliseExpiryDateFilter(baseFilters)
      const res = await getDigitalCodesList(queryParams, filters)
      const composed = getComposedData(res)
      setAllRows(composed || [])
      const apiTotal = typeof res?.totalRecords === 'number' ? res.totalRecords : res?.total
      setTotalRecords(typeof apiTotal === 'number' ? apiTotal : (composed || []).length)
    } finally {
      setLoading(false)
    }
  }, [normaliseExpiryDateFilter, queryParams, rewardId, searchArr])

  useEffect(() => {
    mountedRef.current = true
    loadData()
    return () => {
      mountedRef.current = false
    }
  }, [loadData])

  // With API-based sorting, processed rows are just what the API returned
  const processed = useMemo(() => allRows, [allRows])

  const pageSize = DIGITAL_CODES_PAGE_SIZE
  const pagedRecords = useMemo(() => processed, [processed])

  const onResetAll = useCallback(() => {
    setSelectedRows([])
    setConfirmOpen(false)
    setPendingDeleteIds([])
    // Reset shared table state (filters, pageIndex) similar to useSalesTargets
    tableHandleResetAll()
  }, [tableHandleResetAll])

  const handleSorting = useCallback(({ column, direction }) => {
    if (!column) return

    // Delegate to useDataTable so queryParams (sortBy/order) and API calls stay in sync
    setSortingObj({ column, direction })
  }, [setSortingObj])

  const onRowSelect = useCallback((rows) => {
    setSelectedRows(rows || [])
  }, [])

  const showDownloadEnabled = !!rewardId
  const showUploadEnabled =
    !!rewardId && !(rewardStatus === REWARD_STATUS.EXPIRED || rewardStatus === REWARD_STATUS.REVOKED)
  const anySelected = selectedRows.length > 0
  const selectionHasCustomer = selectedRows.some(r => !!r.customerId)
  const canBulkDelete = anySelected && !selectionHasCustomer

  const handleDownloadTemplate = useCallback(() => {
    const headers = ['Reward ID', 'Reward Name', 'Code', 'Expiry date', 'Code Used']
    const valuesRow = [
      rewardId ? String(rewardId).replaceAll(',', ' ') : '',
      rewardName ? String(rewardName).replaceAll(',', ' ') : '',
      '',
      '',
      '',
    ]
    const csv = [headers.join(','), valuesRow.join(',')].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'digital-codes-template.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [rewardId, rewardName])

  const handleBulkDeleteClick = useCallback(() => {
    const ids = selectedRows.map(r => r.codeId)
    setPendingDeleteIds(ids)
    setConfirmOpen(true)
  }, [selectedRows])

  const handleSingleDeleteClick = useCallback((row) => {
    if (row.customerId) return
    setPendingDeleteIds([row.codeId])
    setConfirmOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    try {
      setLoading(true)
      const res = await deleteDigitalCodes({ codeIds: pendingDeleteIds })
      const composed = getComposedData(res)
      setAllRows(composed || [])
      const apiTotal = typeof res?.totalRecords === 'number' ? res.totalRecords : res?.total
      setTotalRecords(typeof apiTotal === 'number' ? apiTotal : (composed || []).length)
    } finally {
      setLoading(false)
      setSelectedRows([])
      setPendingDeleteIds([])
      setConfirmOpen(false)
    }
  }, [pendingDeleteIds])

  const hasActiveFilters = Array.isArray(searchArr) && searchArr.length > 0

  return {
    // data
    allRows,
    processed,
    pagedRecords,
    totalRecords,
    pageSize,

    // selection & paging
    pageIndex,
    setPageIndex,
    selectedRows,

    // sorting & search
    sortingObj,
    // pass through to CustomDataTable; it will call this with single-field searchObj,
    // and useDataTable will build/maintain the filters array (searchArr) for the API
    setSearchObj: setSearchArry,
    handleSorting,
    onResetAll,

    // actions
    onRowSelect,
    handleDownloadTemplate,
    handleBulkDeleteClick,
    handleSingleDeleteClick,

    // delete modal
    confirmOpen,
    setConfirmOpen,
    handleConfirmDelete,

    // flags
    loading,
    showDownloadEnabled,
    showUploadEnabled,
    canBulkDelete,
    hasActiveFilters,

    // external
    rewardId,
    handleDraft,
  }
}
